import React, {ChangeEvent, forwardRef, InputHTMLAttributes, TextareaHTMLAttributes, useEffect, useRef} from 'react';
import styles from './TextInput.module.scss';

type BaseProps = {
  isFocused?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
};

type InputProps = BaseProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  multiline?: false;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
};

type TextareaProps = BaseProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> & {
  multiline: true;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
};

type TextInputProps = InputProps | TextareaProps;

export default forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextInputProps
>((props, ref) => {

  const {
    className = '',
    isFocused = false,
    ...restProps
  } = props

  const localRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isFocused) {
      localRef.current?.focus();
    }
  }, [isFocused]);

  if (props.multiline) {
    return <textarea
      {...restProps as TextareaProps}
      ref={localRef as React.Ref<HTMLTextAreaElement>}
      className={
        'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-md shadow-xs ' +
        className
      }
    />
  }

  const {size, fullWidth, ...inputProps} = restProps as InputProps;

  return <input
    {...inputProps}
    type={props.type ?? 'text'}
    ref={localRef as React.Ref<HTMLInputElement>}
    className={`${styles.textInput} ${className} ${styles[size ?? '']} ${
      fullWidth ? styles.fullWidth : ''
    }`}
  />
});
